/**
 * TellerCountSelector — the "Leads" stepper under the twin circles (Step 11b, D4).
 *
 * − is disabled at `min` (the picks, at least 1), + at `max` (twins in the round).
 * When nobody is in the round the whole control is disabled and the stored value
 * shows dimmed. The value is announced politely.
 *
 * Two looks over one contract: `row` (the pane, default) and `pill` (the composer's
 * send cluster, home stepper tokens: just − 2 +, the tooltip names the control and
 * screen readers hear the count with its word). `testIdPrefix` keeps
 * the ids unique when both are on screen.
 */

import { useTranslation } from 'react-i18next'
import { Minus, Plus } from 'lucide-react'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface TellerCountSelectorProps {
  value: number
  min: number
  max: number
  isDisabled?: boolean
  onChange: (value: number) => void
  /** `row` is the pane's labelled row; `pill` is the compact composer stepper. */
  variant?: 'row' | 'pill'
  /** Prefix of every data-testid (`<prefix>`, `<prefix>-decrease`, `-value`, `-increase`). */
  testIdPrefix?: string
}

/** Pill step button: 24px visual, 44px hit area (the after box reaches 10px past each edge). */
function pillStepClass(isStepDisabled: boolean): string {
  return cn(
    'relative inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
    "after:absolute after:-inset-2.5 after:content-['']",
    'text-[var(--home-stepper-fg)] hover:text-[var(--home-stepper-fg-hover)]',
    isStepDisabled && 'cursor-not-allowed opacity-40 hover:text-[var(--home-stepper-fg)]',
  )
}

/** Pane step button: 28px visual, 44px hit area (the after box reaches 8px past each edge). */
function stepClass(isStepDisabled: boolean): string {
  return cn(
    'relative flex h-7 w-7 items-center justify-center rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-accent',
    "after:absolute after:-inset-2 after:content-['']",
    isStepDisabled
      ? 'cursor-not-allowed text-twin-chat-muted/30'
      : 'text-twin-chat-muted hover:bg-twin-chat-bubble/50',
  )
}

export function TellerCountSelector(props: TellerCountSelectorProps) {
  const {
    value,
    min,
    max,
    isDisabled = false,
    onChange,
    variant = 'row',
    testIdPrefix = 'teller-count',
  } = props

  const { t } = useTranslation('app')

  const isDecreaseDisabled = isDisabled || value <= min
  const isIncreaseDisabled = isDisabled || value >= max

  if (variant === 'pill') {
    const summary = `${t('roomPane.leads')} · ${t('roomPane.speakCount', { count: value })}`

    // asChild: the trigger must not be a button, since the pill holds the two step buttons.
    return (
      <TooltipProvider delayDuration={400}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              role="group"
              aria-label={t('roomPane.leads')}
              className={cn(
                // Same box as the model select trigger beside it: 32px, input border, page background.
                'inline-flex h-8 shrink-0 items-center rounded-full border border-input bg-background px-1',
                isDisabled && 'opacity-60',
              )}
              data-testid={testIdPrefix}
            >
              <button
                type="button"
                onClick={() => onChange(value - 1)}
                disabled={isDecreaseDisabled}
                aria-label={t('roomPane.fewerLeads')}
                data-testid={`${testIdPrefix}-decrease`}
                className={pillStepClass(isDecreaseDisabled)}
              >
                <Minus className="h-3 w-3" strokeWidth={2.4} />
              </button>
              <span
                aria-live="polite"
                className="min-w-[16px] text-center text-xs font-medium tabular-nums text-[hsl(var(--home-stepper-count-fg))]"
              >
                <span aria-hidden="true" data-testid={`${testIdPrefix}-value`}>
                  {value}
                </span>
                <span className="sr-only" data-testid={`${testIdPrefix}-label`}>
                  {t('roomPane.speakCount', { count: value })}
                </span>
              </span>
              <button
                type="button"
                onClick={() => onChange(value + 1)}
                disabled={isIncreaseDisabled}
                aria-label={t('roomPane.moreLeads')}
                data-testid={`${testIdPrefix}-increase`}
                className={pillStepClass(isIncreaseDisabled)}
              >
                <Plus className="h-3 w-3" strokeWidth={2.4} />
              </button>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" data-testid={`${testIdPrefix}-tooltip`}>
            {summary}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border border-twin-chat-divider/60 px-3 py-2',
        isDisabled && 'opacity-60',
      )}
      data-testid={testIdPrefix}
    >
      <span className="flex-1 text-xs font-medium text-twin-chat-fg">{t('roomPane.leads')}</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(value - 1)}
          disabled={isDecreaseDisabled}
          aria-label={t('roomPane.fewerLeads')}
          data-testid={`${testIdPrefix}-decrease`}
          className={stepClass(isDecreaseDisabled)}
        >
          <Minus className="h-3 w-3" />
        </button>
        <span
          aria-live="polite"
          data-testid={`${testIdPrefix}-value`}
          className="min-w-[1.25rem] text-center text-sm font-medium tabular-nums text-twin-chat-fg"
        >
          {value}
        </span>
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          disabled={isIncreaseDisabled}
          aria-label={t('roomPane.moreLeads')}
          data-testid={`${testIdPrefix}-increase`}
          className={stepClass(isIncreaseDisabled)}
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}
